'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { getAllCourses } from '@/services/courseService';

interface Course {
  id: string;
  title: string;
  instructor_name?: string;
  price: number;
  duration_weeks?: number;
  thumbnail_url?: string;
  average_rating?: string;
  is_active: boolean;
}

interface TransformedCourse {
  id: string;
  title: string;
  instructor: string;
  category: string;
  rating: number;
  students: number;
  duration: string;
  level: string;
  price: number;
  image: string;
  alt: string;
  nextClass: string;
}

const FeaturedCourses = () => {
  const [courses, setCourses] = useState<TransformedCourse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    getAllCourses()
      .then(({ data: coursesData, error }) => {
        if (!mounted) return;

        if (error) {
          console.error('Error fetching courses:', error);
          setLoading(false);
          return;
        }

        // Use real courses if available, fallback to empty array
        const dbCourses = coursesData?.filter((c: Course) => c.is_active).slice(0, 4) || [];
        
        // Transform database courses to match UI format
        const transformedCourses = dbCourses.map((course: Course) => ({
          id: course.id,
          title: course.title,
          instructor: course.instructor_name || "Expert Instructor",
          category: "Professional Development",
          rating: parseFloat(course.average_rating || '4.8'),
          students: 2500,
          duration: `${course.duration_weeks || 8} weeks`,
          level: "Intermediate",
          price: course.price,
          image: course.thumbnail_url || "https://images.unsplash.com/photo-1725800066480-7ccf189e9513",
          alt: `${course.title} course thumbnail`,
          nextClass: "Jan 20, 2026"
        }));

        setCourses(transformedCourses);
        setLoading(false);
      })
      .catch((err) => {
        if (!mounted) return;
        console.error('Error in course fetching:', err);
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Loading state
  if (loading) {
    return (
      <section className="bg-transparent py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="font-headline text-3xl font-bold text-white drop-shadow-lg mb-4">
              Loading Courses...
            </h2>
          </div>
        </div>
      </section>
    );
  }

  // If no courses in database, show message
  if (courses.length === 0) {
    return (
      <section className="bg-transparent py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="font-headline text-3xl font-bold text-white drop-shadow-lg mb-4">
              Featured Courses Coming Soon
            </h2>
            <p className="text-lg text-white/90 drop-shadow-md">
              Check back later for our upcoming courses
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-transparent py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-4 border border-white/20">
            <Icon name="SparklesIcon" size={20} variant="solid" className="text-primary" />
            <span className="text-sm font-semibold text-white">Featured Courses</span>
          </div>
          <h2 className="font-headline text-3xl sm:text-4xl lg:text-5xl font-bold text-white drop-shadow-lg mb-4">
            Start Your Learning Journey Today
          </h2>
          <p className="text-lg text-white/90 max-w-2xl mx-auto drop-shadow-md">
            Explore our most popular courses taught by industry experts with live instruction and hands-on projects.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {courses.map((course) => (
            <Link
              key={course.id}
              href={`/individual-course-page?id=${course.id}`}
              className="group bg-white/10 backdrop-blur-md rounded-xl overflow-hidden shadow-card hover:shadow-brand transition-all duration-300 border border-white/20 hover:border-primary"
            >
              <div className="relative h-48 overflow-hidden">
                <AppImage
                  src={course.image}
                  alt={course.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute top-3 left-3">
                  <span className="bg-brand-conversion text-white text-xs font-semibold px-3 py-1 rounded-full">
                    {course.level}
                  </span>
                </div>
                <div className="absolute top-3 right-3">
                  <span className="bg-white/90 backdrop-blur-sm text-foreground text-xs font-semibold px-3 py-1 rounded-full">
                    ₹{course.price.toLocaleString('en-IN')}
                  </span>
                </div>
              </div>

              <div className="p-5 space-y-3">
                <div className="flex items-center justify-between text-xs text-white/70">
                  <span className="flex items-center space-x-1">
                    <Icon name="TagIcon" size={14} variant="outline" />
                    <span>{course.category}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <Icon name="ClockIcon" size={14} variant="outline" />
                    <span>{course.duration}</span>
                  </span>
                </div>

                <h3 className="font-headline text-lg font-bold text-white group-hover:text-primary transition-colors line-clamp-2 drop-shadow-md">
                  {course.title}
                </h3>

                <p className="text-sm text-white/70 flex items-center space-x-1 drop-shadow-sm">
                  <Icon name="UserIcon" size={14} variant="outline" />
                  <span>{course.instructor}</span>
                </p>

                <div className="flex items-center justify-between pt-2 border-t border-white/20">
                  <div className="flex items-center space-x-1">
                    <Icon name="StarIcon" size={16} variant="solid" className="text-accent" />
                    <span className="text-sm font-semibold text-white">{course.rating}</span>
                    <span className="text-xs text-white/70">({course.students.toLocaleString()})</span>
                  </div>
                  <div className="flex items-center space-x-1 text-xs text-success font-medium">
                    <Icon name="CalendarIcon" size={14} variant="outline" />
                    <span>{course.nextClass}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center">
          <Link
            href="/course-catalog"
            className="inline-flex items-center space-x-2 bg-primary hover:bg-opacity-90 text-white font-cta font-semibold px-8 py-4 rounded-lg transition-all duration-300 shadow-button"
          >
            <span>View All Courses</span>
            <Icon name="ArrowRightIcon" size={20} variant="solid" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedCourses;